import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// ----------------------------------------------------------------------
// TREINCO BRAND TOKENS
// Core brand colors from the Treinco brandbook. Prefer the semantic
// palette below (primary/secondary/info/...) in components; reach for
// BRAND only when a raw brand color is required.
//
// Accessibility rules (brandbook):
//  - NEVER place white text on brand.green (#00DE8F). Use brand.navy
//    instead — this is enforced via contrastText on secondary/success.
//  - brand.sky (#B0D1F4) must NOT be used as body text on white; it is a
//    background / accent color only.
// ----------------------------------------------------------------------
export const BRAND = {
  navy: '#001D3D', // Primary brand color
  green: '#00DE8F', // Signature accent (secondary / success)
  sky: '#B0D1F4', // Light brand blue — backgrounds & accents only
  blue: '#2563A4', // Informational blue
  amber: '#F59E0B', // Warning / attention
  red: '#DC2626', // Error / danger
};

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

// PRIMARY — brand navy
const PRIMARY = {
  lighter: '#134A82',
  light: '#0A3A6B',
  main: '#001D3D',
  dark: '#003465',
  darker: '#001D3D',
  contrastText: '#FFFFFF',
};
// SECONDARY — brand green (signature accent). contrastText is navy: never
// white text on green.
const SECONDARY = {
  lighter: '#80E8C0',
  light: '#40E3A8',
  main: '#00DE8F',
  dark: '#00B574',
  darker: '#008C5A',
  contrastText: '#001D3D',
};
// INFO — informational blue
const INFO = {
  lighter: '#D9E7F5',
  light: '#B0D1F4',
  main: '#2563A4',
  dark: '#134A82',
  darker: '#0E3A66',
  contrastText: '#FFFFFF',
};
// SUCCESS — alias of brand green. contrastText is navy: never white on green.
const SUCCESS = {
  lighter: '#80E8C0',
  light: '#40E3A8',
  main: '#00DE8F',
  dark: '#00B574',
  darker: '#008C5A',
  contrastText: '#001D3D',
};
// WARNING — brand amber. Dark text for contrast on the light amber.
const WARNING = {
  lighter: '#FCEFC7',
  light: '#F8C766',
  main: '#F59E0B',
  dark: '#C2780A',
  darker: '#7A4D05',
  contrastText: GREY[800],
};
// ERROR — brand red
const ERROR = {
  lighter: '#FBD5D5',
  light: '#F08A8A',
  main: '#DC2626',
  dark: '#B91C1C',
  darker: '#7F1D1D',
  contrastText: '#FFFFFF',
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

// Data-visualization palette (brandbook). `colors` is the ordered sequence
// for series; the named ramps are kept for components that request a hue.
const CHART_COLORS = {
  colors: [
    '#001D3D', // navy
    '#00DE8F', // green
    '#2563A4', // blue
    '#F59E0B', // amber
    '#B0D1F4', // sky
    '#8B5CF6', // violet
    '#14B8A6', // teal
    '#64748B', // slate
  ],
  navy: ['#001D3D', '#003465', '#134A82', '#B0D1F4'],
  green: ['#00DE8F', '#40E3A8', '#80E8C0', '#C0F2DC'],
  blue: ['#2563A4', '#5B86BE', '#8FB4D8', '#B0D1F4'],
  amber: ['#F59E0B', '#F8B84D', '#FBD08A', '#FDE7C4'],
  violet: ['#8B5CF6', '#A883F8', '#C6AEFB', '#E3D6FD'],
  teal: ['#14B8A6', '#48C9BB', '#7BD9CF', '#ADE8E2'],
  slate: ['#64748B', '#8693A5', '#A8B2BF', '#C9D0D9'],
  // Legacy aliases (kept for backward-compatibility with chart components)
  yellow: ['#F59E0B', '#F8B84D', '#FBD08A', '#FDE7C4'],
  red: ['#DC2626', '#E55B5B', '#EE9090', '#F7C5C5'],
};

export const MARKER_STATUS_COLORS = {
  online: '#00DE8F',
  offline: '#DC2626',
  unknown: '#919EAB',
  recent: '#F59E0B',
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  brand: { ...BRAND },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;
